import { useGlobalToasts } from '@src/hooks/Toast/useGlobalToasts';
import { useCallback } from 'react';
import { copyText } from '../../../../utils/copyText';
import { NotificationVariants } from '../../../Notification';
import type { WorkflowSummaryRow, WorkflowSummarySection } from './types';

export function getSummaryRowsCopyText(rows: WorkflowSummaryRow[]): string {
  return rows
    .filter(row => row.value)
    .map(row => `${row.label}: ${row.value}`)
    .join(' | ');
}

export function getSummarySectionsCopyText(sections: WorkflowSummarySection[]): string {
  return sections
    .flatMap(section => section.rows.filter(row => row.value).map(row => `${section.title} ${row.label}: ${row.value}`))
    .join(' | ');
}

/**
 * Returns copy handlers for workflow summary rows and sections.
 * Each handler copies the text to clipboard and shows a toast on success or failure.
 * Wrap callsites in useCallback when passing as a prop to avoid recreating the closure
 * on every render, e.g. `useCallback(() => copyRows(rows), [copyRows, rows])`.
 */
export function useWorkflowCopy() {
  const { add: addToast } = useGlobalToasts();

  const copyRows = useCallback(
    async (rows: WorkflowSummaryRow[]) => {
      const text = getSummaryRowsCopyText(rows);
      if (!text) {
        addToast({ text: 'No summary details available to copy.', variant: NotificationVariants.Negative });
        return;
      }
      await copyText(text);
      addToast({ text: 'Summary copied.', variant: NotificationVariants.Positive });
    },
    [addToast]
  );

  const copySections = useCallback(
    async (sections: WorkflowSummarySection[], successText = 'Details copied.') => {
      const text = getSummarySectionsCopyText(sections);
      if (!text) {
        addToast({ text: 'No details available to copy.', variant: NotificationVariants.Negative });
        return;
      }
      await copyText(text);
      addToast({ text: successText, variant: NotificationVariants.Positive });
    },
    [addToast]
  );

  return { copyRows, copySections };
}
